import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatDate, formatRelative } from '../ui/format';
import { ErrorState, Loading } from '../ui/Page';
import { getSprintReport, type BurndownPoint, type SprintState } from './api';

/**
 * The sprint as a team reviews it: the line, and who did what to it.
 *
 * Two lines rather than one — what is left, and how much is in scope — because a sprint that ends
 * on time after taking on half again as much work is not the same as one that ends on time. The
 * dotted line is where an even burn from the starting scope would be.
 */
export function SprintReportPanel({ sprintId }: { sprintId: string }): React.JSX.Element {
  const report = useQuery({
    queryKey: ['sprint-report', sprintId],
    queryFn: () => getSprintReport(sprintId),
  });

  if (report.isLoading) return <Loading />;
  if (report.error)
    return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;

  const data = report.data!;

  return (
    <div className="sprint-report">
      <Burndown points={data.burndown} state={data.sprint.state} />

      <ul className="dash__rows">
        {data.ledger.slice(0, 12).map((entry, index) => (
          <li key={`${entry.taskId}-${entry.occurredAt}-${index}`}>
            <span className="dash__row">
              <span className="cell-stack">
                <span>
                  <strong>{entry.actorName ?? t('audit.actor.SYSTEM')}</strong>{' '}
                  {t(`sprint.ledger.${entry.kind}`)} <span className="muted">{entry.title}</span>
                </span>
                <span className="muted small">
                  {formatRelative(entry.occurredAt)}
                  {entry.storyPoints !== null
                    ? ` · ${entry.storyPoints} ${t('sprint.pointsShort')}`
                    : ''}
                </span>
              </span>
            </span>
          </li>
        ))}
      </ul>
      {data.ledger.length === 0 && <p className="muted small">{t('sprint.noLedger')}</p>}
    </div>
  );
}

function Burndown({
  points,
  state,
}: {
  points: BurndownPoint[];
  state: SprintState;
}): React.JSX.Element {
  /*
   * A line needs two days. On the first day of a sprint there is one point, and drawing it would
   * be a chart that says nothing — so the panel says which of the two reasons applies rather than
   * blaming the sprint for not having started when it has.
   */
  if (points.length < 2) {
    return (
      <p className="muted small">
        {state === 'PLANNED' ? t('sprint.noBurndown') : t('sprint.burndownDayOne')}
      </p>
    );
  }

  const width = 640;
  const height = 180;
  const pad = { top: 14, right: 10, bottom: 24, left: 10 };
  const max = Math.max(
    ...points.map((point) => Math.max(point.scope, point.remaining, point.ideal)),
    1,
  );
  const stepX = (width - pad.left - pad.right) / (points.length - 1);
  const x = (index: number): number => pad.left + index * stepX;
  const y = (value: number): number =>
    pad.top + (height - pad.top - pad.bottom) * (1 - value / max);
  const path = (pick: (point: BurndownPoint) => number): string =>
    points
      .map(
        (point, index) =>
          `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(pick(point)).toFixed(1)}`,
      )
      .join(' ');

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="chart__svg"
        role="img"
        aria-label={t('sprint.burndown')}
      >
        {[0.25, 0.5, 0.75].map((fraction) => (
          <line
            key={fraction}
            x1={pad.left}
            x2={width - pad.right}
            y1={y(max * fraction)}
            y2={y(max * fraction)}
            className="chart__grid"
          />
        ))}
        <path d={path((point) => point.ideal)} className="chart__line chart__line--ideal" />
        <path d={path((point) => point.scope)} className="chart__line chart__line--scope" />
        <path d={path((point) => point.remaining)} className="chart__line" />
        {points.map((point, index) => (
          <g key={point.day}>
            <circle cx={x(index)} cy={y(point.remaining)} r={2.5} className="chart__dot" />
            <rect
              x={x(index) - stepX / 2}
              y={pad.top}
              width={stepX}
              height={height - pad.top - pad.bottom}
              fill="transparent"
            >
              <title>{`${formatDate(point.day)} · ${t('sprint.remaining')} ${point.remaining} · ${t('sprint.scope')} ${point.scope}`}</title>
            </rect>
          </g>
        ))}
        <text x={pad.left} y={height - 6} className="chart__axis">
          {formatDate(points[0]!.day)}
        </text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" className="chart__axis">
          {formatDate(points.at(-1)!.day)}
        </text>
      </svg>

      <p className="muted small chart__legend">
        <span className="legend legend--remaining" /> {t('sprint.remaining')}
        <span className="legend legend--scope" /> {t('sprint.scope')}
        <span className="legend legend--ideal" /> {t('sprint.ideal')}
      </p>
    </div>
  );
}
